/**
 * DNA Archive v2 — Animation Engine
 * Single rAF scroll loop, modular architecture, IntersectionObserver lifecycle
 * Modules: reveal, parallax, kenBurns, carousel, ticker, gradientDrift, heroEntrance
 */

(function () {
  'use strict';

  // ==============================
  // CORE CONFIG
  // ==============================
  var config = {
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    isMobile: window.innerWidth < 750,
    scrollY: 0,
    vh: window.innerHeight,
    ticking: false
  };

  // ==============================
  // SCROLL ENGINE — single rAF loop
  // ==============================
  var scrollTargets = [];

  function scrollTick() {
    config.scrollY = window.scrollY;
    config.vh = window.innerHeight;

    for (var i = 0; i < scrollTargets.length; i++) {
      if (scrollTargets[i].active) {
        scrollTargets[i].update(config.scrollY, config.vh);
      }
    }
    config.ticking = false;
  }

  function onScroll() {
    if (!config.ticking) {
      config.ticking = true;
      requestAnimationFrame(scrollTick);
    }
  }

  function registerScrollTarget(target) {
    scrollTargets.push(target);
  }


  // ==============================
  // MODULE: Scroll Reveal
  // ==============================
  function initScrollReveal() {
    var elements = document.querySelectorAll('.dna-reveal');
    if (!elements.length) return;

    if (config.reducedMotion) {
      elements.forEach(function (el) { el.classList.add('dna-reveal--visible'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('dna-reveal--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
    );

    elements.forEach(function (el) { observer.observe(el); });
  }


  // ==============================
  // MODULE: Stagger Reveal
  // ==============================
  function initStaggerReveal() {
    var containers = document.querySelectorAll('.dna-stagger');
    if (!containers.length) return;

    if (config.reducedMotion) {
      containers.forEach(function (c) { c.classList.add('dna-stagger--visible'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('dna-stagger--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    containers.forEach(function (c) { observer.observe(c); });
  }


  // ==============================
  // MODULE: Parallax
  // ==============================
  function initParallax() {
    if (config.reducedMotion || config.isMobile) return;

    var narrativeBgs = document.querySelectorAll('.dna-narrative__bg img');

    var targets = [];

    narrativeBgs.forEach(function (img) {
      targets.push({ el: img, speed: 0.03, parent: img.closest('.dna-narrative') });
    });

    if (!targets.length) return;

    registerScrollTarget({
      active: true,
      update: function (scrollY, vh) {
        for (var i = 0; i < targets.length; i++) {
          var t = targets[i];
          var rect = t.parent.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > vh) continue;

          var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
          var offset = progress * t.speed * vh;
          t.el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
        }
      }
    });
  }


  // ==============================
  // MODULE: Kinetic Hero Heading
  // ==============================
  function initKineticHero() {
    if (config.reducedMotion) return;

    var heading = document.querySelector('.dna-hero__heading');
    var tagline = document.querySelector('.dna-hero__tagline');
    var hero = document.querySelector('.dna-hero');
    if (!heading || !hero) return;

    registerScrollTarget({
      active: true,
      update: function (scrollY, vh) {
        var rect = hero.getBoundingClientRect();
        var progress = Math.max(0, Math.min(1, -rect.top / (rect.height * 0.6)));
        heading.style.transform = 'translateY(' + (progress * -30).toFixed(1) + 'px)';
        heading.style.opacity = (1 - progress * 0.7).toFixed(2);
        if (tagline) {
          tagline.style.transform = 'translateY(' + (progress * -15).toFixed(1) + 'px)';
          tagline.style.opacity = (1 - progress * 0.9).toFixed(2);
        }
      }
    });
  }


  // ==============================
  // MODULE: Hero Word Stagger
  // ==============================
  function initHeroWordStagger() {
    if (config.reducedMotion) return;

    var headings = document.querySelectorAll('.dna-hero__heading');
    headings.forEach(function (heading) {
      // Only process if not already wrapped
      if (heading.querySelector('.dna-word')) return;

      var text = heading.textContent.trim();
      var words = text.split(/\s+/);
      heading.innerHTML = '';
      heading.style.animation = 'none'; // Disable CSS entrance, use JS instead
      heading.style.opacity = '1';

      words.forEach(function (word, i) {
        var span = document.createElement('span');
        span.className = 'dna-word';
        span.textContent = word;
        span.style.transitionDelay = (i * 0.12) + 's';
        heading.appendChild(span);

        // Add space between words
        if (i < words.length - 1) {
          heading.appendChild(document.createTextNode(' '));
        }
      });

      // Trigger reveal after a brief delay
      setTimeout(function () {
        var wordEls = heading.querySelectorAll('.dna-word');
        wordEls.forEach(function (w) {
          w.classList.add('dna-word--visible');
        });
      }, 400);
    });
  }


  // ==============================
  // MODULE: Hero Slideshow
  // ==============================
  function initHeroSlideshow() {
    var heroes = document.querySelectorAll('.dna-hero[data-slideshow]');
    heroes.forEach(function (hero) {
      var slides = hero.querySelectorAll('.dna-hero__slide');
      if (slides.length < 2) return;

      var currentIndex = 0;
      var interval = parseInt(hero.dataset.slideInterval, 10) || 8000;

      function showSlide(index) {
        slides.forEach(function (s, i) {
          s.classList.toggle('dna-hero__slide--active', i === index);
        });
        currentIndex = index;
      }

      // Auto-advance
      var timer = setInterval(function () {
        var next = (currentIndex + 1) % slides.length;
        showSlide(next);
      }, interval);

      // Pause when off-screen
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            timer = setInterval(function () {
              var next = (currentIndex + 1) % slides.length;
              showSlide(next);
            }, interval);
          } else {
            clearInterval(timer);
          }
        });
      }, { threshold: 0.2 });

      observer.observe(hero);
      showSlide(0);
    });
  }


  // ==============================
  // MODULE: Lookbook Carousel
  // ==============================
  function initCrate() {
    var crates = document.querySelectorAll('.dna-crate');
    crates.forEach(function (crate) {
      var track = crate.querySelector('[data-crate-track]');
      var cards = crate.querySelectorAll('[data-crate-card]');
      var dots = crate.querySelectorAll('[data-crate-dot]');
      var viewport = crate.querySelector('.dna-crate__viewport');
      var counterEl = crate.querySelector('[data-crate-counter-current]');
      var hint = crate.querySelector('[data-crate-hint]');
      var thumbStrips = crate.querySelectorAll('[data-crate-thumbs]');

      if (!track || cards.length === 0) return;

      var activeIndex = 0;
      var isDragging = false;
      var startX = 0;
      var currentTranslate = 0;
      var prevTranslate = 0;
      var dragStartTime = 0;
      var dragStartTranslate = 0;
      var autoAdvanceTimer = null;
      var autoAdvanceDuration = 5000;
      var userHasInteracted = false;

      function getCardStep() {
        var card = cards[0];
        var style = window.getComputedStyle(track);
        var gap = parseFloat(style.gap) || 20;
        return card.offsetWidth + gap;
      }

      function getSnapPosition(index) {
        return -(index * getCardStep());
      }

      function updateCounter(index) {
        if (!counterEl) return;
        var num = (index + 1).toString();
        if (num.length < 2) num = '0' + num;
        counterEl.textContent = num;
      }

      function setActiveCard(index) {
        if (index < 0) index = 0;
        if (index >= cards.length) index = cards.length - 1;
        activeIndex = index;

        cards.forEach(function (c, i) {
          c.classList.remove('dna-crate__card--active', 'dna-crate__card--prev', 'dna-crate__card--next');
          if (i === index) c.classList.add('dna-crate__card--active');
          else if (i === index - 1) c.classList.add('dna-crate__card--prev');
          else if (i === index + 1) c.classList.add('dna-crate__card--next');
        });

        dots.forEach(function (d, i) {
          var wasActive = d.classList.contains('dna-crate__dot--active');
          d.classList.toggle('dna-crate__dot--active', i === index);
          if (i === index && !wasActive) {
            var bar = d.querySelector('.dna-crate__dot-progress');
            if (bar) {
              bar.style.transition = 'none';
              bar.style.width = '0%';
              void bar.offsetHeight;
              bar.style.transition = '';
              requestAnimationFrame(function () { bar.style.width = ''; });
            }
          } else if (i !== index) {
            var bar2 = d.querySelector('.dna-crate__dot-progress');
            if (bar2) { bar2.style.transition = 'none'; bar2.style.width = '0%'; }
          }
        });

        thumbStrips.forEach(function (strip, i) {
          strip.classList.toggle('dna-crate__thumb-strip--active', i === index);
        });

        updateCounter(index);
      }

      function snapTo(index, useDecelerate) {
        if (index < 0) index = 0;
        if (index >= cards.length) index = cards.length - 1;

        setActiveCard(index);
        var target = getSnapPosition(index);
        currentTranslate = target;
        prevTranslate = target;

        track.classList.remove('dna-crate__track--snapping', 'dna-crate__track--decelerating');
        void track.offsetHeight;
        track.classList.add(useDecelerate ? 'dna-crate__track--decelerating' : 'dna-crate__track--snapping');
        track.style.transform = 'translate3d(' + target + 'px, 0, 0)';

        restartAutoAdvance();
      }

      function setPosition(x) {
        track.classList.remove('dna-crate__track--snapping', 'dna-crate__track--decelerating');
        track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
      }

      function getClosestIndex(translate) {
        var step = getCardStep();
        var index = Math.round(-translate / step);
        return Math.max(0, Math.min(cards.length - 1, index));
      }

      // Auto-advance
      function startAutoAdvance() {
        stopAutoAdvance();
        if (userHasInteracted) return;
        autoAdvanceTimer = setTimeout(function () {
          var next = (activeIndex + 1) % cards.length;
          snapTo(next, false);
        }, autoAdvanceDuration);
      }

      function stopAutoAdvance() {
        if (autoAdvanceTimer) { clearTimeout(autoAdvanceTimer); autoAdvanceTimer = null; }
      }

      function restartAutoAdvance() {
        stopAutoAdvance();
        if (!userHasInteracted) startAutoAdvance();
      }

      // Card parallax on drag
      function updateCardParallax(dragOffset) {
        if (config.reducedMotion) return;
        cards.forEach(function (card) {
          var img = card.querySelector('.dna-crate__card-image img');
          if (!img) return;
          var isActive = card.classList.contains('dna-crate__card--active');
          var parallaxAmount = dragOffset * -0.04;
          var baseScale = isActive ? 1.05 : 1;
          img.style.transform = 'scale(' + baseScale + ') translateX(' + parallaxAmount.toFixed(1) + 'px)';
        });
      }

      // Pointer events
      function onPointerDown(e) {
        if (e.button && e.button !== 0) return;
        isDragging = true;
        startX = (e.type === 'touchstart') ? e.touches[0].clientX : e.clientX;
        dragStartTime = Date.now();
        dragStartTranslate = currentTranslate;
        track.classList.remove('dna-crate__track--snapping', 'dna-crate__track--decelerating');
        stopAutoAdvance();
        if (!userHasInteracted) {
          userHasInteracted = true;
          if (hint) hint.classList.replace('dna-crate__hint--visible', 'dna-crate__hint--hidden');
        }
      }

      function onPointerMove(e) {
        if (!isDragging) return;
        var clientX = (e.type === 'touchmove') ? e.touches[0].clientX : e.clientX;
        var diff = clientX - startX;
        currentTranslate = prevTranslate + diff;

        var minTranslate = getSnapPosition(cards.length - 1);
        if (currentTranslate > 0) {
          currentTranslate = currentTranslate * 0.3;
        } else if (currentTranslate < minTranslate) {
          currentTranslate = minTranslate + (currentTranslate - minTranslate) * 0.3;
        }

        setPosition(currentTranslate);
        updateCardParallax(diff);

        var liveIndex = getClosestIndex(currentTranslate);
        if (liveIndex !== activeIndex) setActiveCard(liveIndex);
      }

      function onPointerUp() {
        if (!isDragging) return;
        isDragging = false;

        var elapsed = Date.now() - dragStartTime;
        var distance = currentTranslate - dragStartTranslate;
        var velocity = distance / Math.max(elapsed, 1);

        var targetIndex;
        if (Math.abs(velocity) > 0.35 && elapsed < 350) {
          targetIndex = velocity > 0 ? activeIndex - 1 : activeIndex + 1;
        } else {
          targetIndex = getClosestIndex(currentTranslate);
        }

        prevTranslate = currentTranslate;
        snapTo(targetIndex, Math.abs(velocity) > 0.35);
        setTimeout(function () { updateCardParallax(0); }, 100);
      }

      // Bind events
      viewport.addEventListener('mousedown', onPointerDown);
      viewport.addEventListener('touchstart', onPointerDown, { passive: true });
      window.addEventListener('mousemove', onPointerMove);
      window.addEventListener('touchmove', onPointerMove, { passive: false });
      window.addEventListener('mouseup', onPointerUp);
      window.addEventListener('touchend', onPointerUp);

      viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });
      viewport.addEventListener('click', function (e) {
        if (Math.abs(currentTranslate - dragStartTranslate) > 5) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      // Dot navigation
      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          userHasInteracted = true;
          if (hint) hint.classList.replace('dna-crate__hint--visible', 'dna-crate__hint--hidden');
          var idx = parseInt(dot.getAttribute('data-crate-dot'), 10);
          snapTo(idx, false);
        });
      });

      // Keyboard navigation
      crate.setAttribute('tabindex', '0');
      crate.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault(); userHasInteracted = true; snapTo(activeIndex + 1, false);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault(); userHasInteracted = true; snapTo(activeIndex - 1, false);
        }
      });

      // Initial position
      setActiveCard(0);
      requestAnimationFrame(function () {
        var initial = getSnapPosition(0);
        track.style.transform = 'translate3d(' + initial + 'px, 0, 0)';
        currentTranslate = initial;
        prevTranslate = initial;
      });

      // Show drag hint
      if (hint && !config.reducedMotion) {
        setTimeout(function () {
          if (!userHasInteracted) hint.classList.add('dna-crate__hint--visible');
        }, 1500);
        setTimeout(function () {
          if (hint.classList.contains('dna-crate__hint--visible')) {
            hint.classList.replace('dna-crate__hint--visible', 'dna-crate__hint--hidden');
          }
        }, 6000);
      }

      // Start auto-advance
      startAutoAdvance();

      // Pause auto-advance off-screen
      var sectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !userHasInteracted) startAutoAdvance();
          else stopAutoAdvance();
        });
      }, { threshold: 0.3 });
      sectionObserver.observe(crate);

      // Recalculate on resize
      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          var target = getSnapPosition(activeIndex);
          currentTranslate = target;
          prevTranslate = target;
          track.classList.remove('dna-crate__track--snapping', 'dna-crate__track--decelerating');
          track.style.transform = 'translate3d(' + target + 'px, 0, 0)';
        }, 150);
      });
    });
  }


  // ==============================
  // MODULE: Ticker
  // ==============================
  function initTicker() {
    var tickers = document.querySelectorAll('.dna-ticker__track');
    tickers.forEach(function (track) {
      if (track.dataset.duplicated) return;
      track.innerHTML = track.innerHTML + track.innerHTML;
      track.dataset.duplicated = 'true';

      // Pause on hover
      var ticker = track.closest('.dna-ticker');
      if (ticker) {
        ticker.addEventListener('mouseenter', function () {
          track.style.animationPlayState = 'paused';
        });
        ticker.addEventListener('mouseleave', function () {
          track.style.animationPlayState = 'running';
        });
      }
    });
  }


  // ==============================
  // MODULE: Vault Card Hover
  // ==============================
  function initVaultCards() {
    if (config.reducedMotion) return;

    var items = document.querySelectorAll('.dna-vault__item');
    items.forEach(function (item) {
      // 3D tilt on hover (desktop only)
      if (!config.isMobile) {
        item.addEventListener('mousemove', function (e) {
          var rect = item.getBoundingClientRect();
          var x = (e.clientX - rect.left) / rect.width;
          var y = (e.clientY - rect.top) / rect.height;
          var tiltX = (y - 0.5) * -6;
          var tiltY = (x - 0.5) * 6;
          item.style.transform = 'translateY(-6px) perspective(600px) rotateX(' + tiltX.toFixed(1) + 'deg) rotateY(' + tiltY.toFixed(1) + 'deg)';
        });

        item.addEventListener('mouseleave', function () {
          item.style.transform = '';
        });
      }
    });
  }


  // ==============================
  // MODULE: Ambient Gradient Drift
  // ==============================
  function initAmbientDrift() {
    if (config.reducedMotion) return;

    var driftSections = document.querySelectorAll('.dna-mood--drift');
    if (!driftSections.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.remove('dna-drift--paused');
        } else {
          entry.target.classList.add('dna-drift--paused');
        }
      });
    }, { threshold: 0.05 });

    driftSections.forEach(function (section) {
      section.classList.add('dna-drift--paused');
      observer.observe(section);
    });
  }


  // ==============================
  // MODULE: Stat Counters
  // ==============================
  function initStatCounters() {
    var containers = document.querySelectorAll('[data-dna-counters]');
    if (!containers.length) return;

    // Reduced motion: show final values immediately
    if (config.reducedMotion) {
      containers.forEach(function (c) {
        var nums = c.querySelectorAll('[data-count-to]');
        nums.forEach(function (el) {
          var target = parseInt(el.getAttribute('data-count-to'), 10);
          var suffix = el.getAttribute('data-count-suffix') || '';
          el.textContent = target + suffix;
        });
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var nums = entry.target.querySelectorAll('[data-count-to]');
        nums.forEach(function (el) {
          var target = parseInt(el.getAttribute('data-count-to'), 10);
          var suffix = el.getAttribute('data-count-suffix') || '';
          var duration = 2000;
          var startTime = null;
          function step(timestamp) {
            if (!startTime) startTime = timestamp;
            var progress = Math.min((timestamp - startTime) / duration, 1);
            var eased = 1 - Math.pow(1 - progress, 3);
            el.textContent = Math.floor(eased * target) + suffix;
            if (progress < 1) requestAnimationFrame(step);
          }
          requestAnimationFrame(step);
        });
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.3 });

    containers.forEach(function (c) { observer.observe(c); });
  }


  // ==============================
  // MODULE: Red Divider Draw
  // ==============================
  function initDividerDraw() {
    if (config.reducedMotion) return;

    var dividers = document.querySelectorAll('.dna-divider--red');
    if (!dividers.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('dna-divider--animated');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    dividers.forEach(function (d) { observer.observe(d); });
  }


  // ==============================
  // MODULE: Collection Grid Stagger Reveal
  // ==============================
  function initGridStagger() {
    if (config.reducedMotion) return;

    var items = document.querySelectorAll('.product-grid__item');
    if (!items.length) return;

    // Estimate columns from grid layout
    var cols = config.isMobile ? 2 : 3;
    var firstItem = items[0];
    if (firstItem && firstItem.parentElement) {
      var gridStyle = getComputedStyle(firstItem.parentElement);
      var templateCols = gridStyle.getPropertyValue('grid-template-columns').split(' ').length;
      if (templateCols > 1) cols = templateCols;
    }

    items.forEach(function (item) {
      item.classList.add('dna-grid-reveal');
    });

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var match = entry.target.className.match(/item--(\d+)/);
          var idx = match ? parseInt(match[1], 10) : 0;
          var col = idx % cols;
          entry.target.style.transitionDelay = (col * 0.08) + 's';
          entry.target.classList.add('dna-grid-reveal--visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -40px 0px' });

    items.forEach(function (item) { observer.observe(item); });
  }


  // ==============================
  // MODULE: Sticky Filter Observer
  // ==============================
  function initStickyFilter() {
    var facets = document.querySelector('.collection-wrapper .facets-block-wrapper');
    if (!facets) return;

    var sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    sentinel.style.pointerEvents = 'none';
    sentinel.setAttribute('aria-hidden', 'true');
    facets.parentNode.insertBefore(sentinel, facets);

    var observer = new IntersectionObserver(function (entries) {
      facets.classList.toggle('dna-filter--stuck', !entries[0].isIntersecting);
    }, { threshold: 0 });

    observer.observe(sentinel);
  }


  // ==============================
  // INIT
  // ==============================
  function init() {
    initScrollReveal();
    initStaggerReveal();
    initParallax();
    initKineticHero();
    initHeroWordStagger();
    initHeroSlideshow();
    initCrate();
    initTicker();
    initVaultCards();
    initStatCounters();
    initDividerDraw();
    initAmbientDrift();
    initGridStagger();
    initStickyFilter();

    // Start scroll engine
    if (!config.reducedMotion) {
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  // Handle resize
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      config.isMobile = window.innerWidth < 750;
      config.vh = window.innerHeight;
    }, 200);
  });

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section render (Theme Editor)
  document.addEventListener('shopify:section:load', init);
})();
